import type { QueryKey } from '@tanstack/react-query';
import { MessagingChannelType } from '@treadinc/horizon-api-spec';

import { Nullable } from '~types/Nullable';

export type MessagingTargetChannelType =
  | MessagingChannelType.JOB
  | MessagingChannelType.ORDER;

// Root key: keep child keys rooted here so one prefix invalidation reaches all.
export const MESSAGING_QUERY_KEY = 'messaging';

// Not company-scoped: a reader and a writer keying off different companies would miss each other.
export const messagingTranscriptQueryKey = (channelId: Nullable<string>) => [
  MESSAGING_QUERY_KEY,
  'messages',
  channelId,
];

export const MESSAGING_CHANNEL_QUERY_KEY = [MESSAGING_QUERY_KEY, 'channel'] as const;

export const messagingChannelQueryKey = (
  companyId: Nullable<string>,
  channelId: Nullable<string>,
) => [...MESSAGING_CHANNEL_QUERY_KEY, companyId, channelId];

export const MESSAGING_TARGET_CHANNEL_QUERY_KEY = [
  MESSAGING_QUERY_KEY,
  'targetChannel',
] as const;

// Outside the channels prefix, which the list bump walks as paged channel lists.
export const messagingTargetChannelQueryKey = (
  channelType: MessagingTargetChannelType,
  targetId: Nullable<string>,
) => [...MESSAGING_TARGET_CHANNEL_QUERY_KEY, channelType, targetId];

export const messagingMessageReadersOfChannelQueryKey = (channelId: string) => [
  MESSAGING_QUERY_KEY,
  'readers',
  channelId,
];

export const messagingMessageReadersQueryKey = (channelId: string, messageId: string) => [
  ...messagingMessageReadersOfChannelQueryKey(channelId),
  messageId,
];

// Outside the channels prefix too, and session-scoped like the endpoint, so it carries no company.
export const MESSAGING_UNREAD_COUNTS_QUERY_KEY = [
  MESSAGING_QUERY_KEY,
  'unreadCounts',
] as const;

type MessagingChannelListFilter = {
  archived?: boolean;
  channelType?: MessagingChannelType;
  hasUnread?: boolean;
  jobOrderId?: string;
  searchQuery?: string;
};

export const MESSAGING_CHANNELS_QUERY_KEY = [MESSAGING_QUERY_KEY, 'channels'] as const;

export const messagingChannelsQueryKey = (filter: MessagingChannelListFilter) =>
  [...MESSAGING_CHANNELS_QUERY_KEY, filter] as const;

// Keyed as a job list so the bump skips it for messages on other kinds of channel.
export const messagingOrderJobChannelsQueryKey = (orderId: string) =>
  messagingChannelsQueryKey({
    channelType: MessagingChannelType.JOB,
    jobOrderId: orderId,
  });

const isMessagingChannelListFilter = (
  value: unknown,
): value is MessagingChannelListFilter => {
  if (typeof value !== 'object' || value === null) return false;

  const hasBooleanArchived =
    !('archived' in value) ||
    value.archived === undefined ||
    typeof value.archived === 'boolean';
  const hasKnownChannelType =
    !('channelType' in value) ||
    value.channelType === undefined ||
    Object.values(MessagingChannelType).some(
      (channelType) => channelType === value.channelType,
    );
  const hasBooleanHasUnread =
    !('hasUnread' in value) ||
    value.hasUnread === undefined ||
    typeof value.hasUnread === 'boolean';
  const hasStringJobOrderId =
    !('jobOrderId' in value) ||
    value.jobOrderId === undefined ||
    typeof value.jobOrderId === 'string';
  const hasStringSearchQuery =
    !('searchQuery' in value) ||
    value.searchQuery === undefined ||
    typeof value.searchQuery === 'string';

  return (
    hasBooleanArchived &&
    hasKnownChannelType &&
    hasBooleanHasUnread &&
    hasStringJobOrderId &&
    hasStringSearchQuery
  );
};

/** The filter a cached channel list was read with, or nothing for a key of another shape. */
const messagingChannelListFilterOf = (channelListKey: QueryKey) => {
  const [, , filter] = channelListKey;

  return isMessagingChannelListFilter(filter) ? filter : undefined;
};

/**
 * Whether a cached channel list's filter leaves the channel out, so re-reading it cannot show it.
 * Unread is left out on purpose: a conversation read from the Unread list stays listed until the
 * next fetch, and a new message is exactly what brings one in.
 */
export const isChannelFilteredOutOfList = (
  channelListKey: QueryKey,
  channel: { channelType: MessagingChannelType; isArchived: boolean },
) => {
  const listFilter = messagingChannelListFilterOf(channelListKey);
  const isFilteredToAnotherType =
    listFilter?.channelType !== undefined &&
    listFilter.channelType !== channel.channelType;
  const isFilteredToAnotherArchivedState =
    listFilter?.archived !== undefined && listFilter.archived !== channel.isArchived;

  return isFilteredToAnotherType || isFilteredToAnotherArchivedState;
};
